import React, { useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const AboutContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 6rem 1rem;
`;

const PageTitle = styled(motion.h1)`
  font-size: 3rem;
  margin-bottom: 2rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 2.5rem;
  }
`;

const Section = styled.section`
  margin-bottom: 4rem;
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 1.5rem;
  color: ${({ theme }) => theme.colors.primary};
`;

const Paragraph = styled.p`
  font-size: 1.125rem;
  line-height: 1.7;
  margin-bottom: 1.5rem;
  color: ${({ theme }) => theme.colors.dark};
`;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-top: 3rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const TeamMember = styled.div`
  text-align: center;
`;

const TeamMemberImage = styled.img`
  width: 150px;
  height: 150px;
  border-radius: ${({ theme }) => theme.radii.full};
  object-fit: cover;
  margin-bottom: 1rem;
`;

const TeamMemberName = styled.h3`
  font-size: 1.25rem;
  margin-bottom: 0.5rem;
`;

const TeamMemberPosition = styled.p`
  color: ${({ theme }) => theme.colors.gray};
  margin-bottom: 0.5rem;
`;

const AboutPage: React.FC = () => {
  useEffect(() => {
    document.title = 'About Us | FylePrep';
  }, []);

  const teamMembers = [
    {
      name: 'Alex Johnson',
      position: 'CEO & Co-Founder',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=256&q=80',
    },
    {
      name: 'Samantha Lee',
      position: 'CTO & Co-Founder',
      image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=256&q=80',
    },
    {
      name: 'David Chen',
      position: 'Head of Product',
      image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=256&q=80',
    },
  ];

  return (
    <AboutContainer>
      <PageTitle
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        About FylePrep
      </PageTitle>
      
      <Section>
        <SectionTitle>Our Mission</SectionTitle>
        <Paragraph>
          At FylePrep, we believe that tax preparation should be efficient, accurate, and stress-free for accounting professionals and their clients. 
          Our mission is to democratize data analytics by providing powerful, yet intuitive tools that transform raw data into 
          meaningful insights without requiring a team of data scientists.
        </Paragraph>
        <Paragraph>
          Founded in 2023, FylePrep was born out of the frustration of seeing CPAs and tax professionals struggle with inefficient document collection and organization processes. 
          We saw that while companies were collecting more data than ever before, they lacked the tools and expertise to turn 
          that data into actionable business intelligence.
        </Paragraph>
      </Section>
      
      <Section>
        <SectionTitle>Our Approach</SectionTitle>
        <Paragraph>
          We take a human-centered approach to data analytics. Our platform is designed to be powerful enough for data 
          professionals but accessible enough for business users. We focus on automating the complex parts of data analysis 
          while keeping humans in control of the decision-making process.
        </Paragraph>
        <Paragraph>
          Our technology combines cutting-edge machine learning with intuitive design to identify patterns, detect anomalies, 
          and surface insights that might otherwise go unnoticed. But we don't just provide the tools—we partner with our 
          clients to ensure they're getting the most value from their data.
        </Paragraph>
      </Section>
      
      <Section>
        <SectionTitle>Our Team</SectionTitle>
        <Paragraph>
          FylePrep is powered by a diverse team of tax experts, software engineers, designers, and customer success specialists who share 
          a passion for making data more accessible and useful. We bring together expertise from some of the world's leading 
          technology companies and research institutions.
        </Paragraph>
        
        <TeamGrid>
          {teamMembers.map((member, index) => (
            <TeamMember key={index}>
              <TeamMemberImage src={member.image} alt={member.name} />
              <TeamMemberName>{member.name}</TeamMemberName>
              <TeamMemberPosition>{member.position}</TeamMemberPosition>
            </TeamMember>
          ))}
        </TeamGrid>
      </Section>
    </AboutContainer>
  );
};

export default AboutPage;
